import React, { useCallback, useEffect, useState } from "react";
import { Divider, QRCode } from "antd";
import {
  CalendarOutlined,
  ExclamationCircleOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  GiftOutlined,
  MedicineBoxOutlined,
  QrcodeOutlined,
} from "@ant-design/icons";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import CommonIcons from "@base/assets/icons";
import CommonImages from "@base/assets/images";
import { EAppointmentStatus, EAppointmentType, EOrderType } from "@base/enums";
import useCommonSettings from "@base/hooks/useCommonSettings";
import useToggle from "@base/hooks/useToggle";
import { IAppointment } from "@base/modules/appointment/appointment.interface";
import appointmentService from "@base/modules/appointment/appointmentService";
import { IPatientRecord } from "@base/modules/user/profile/profile.interface";
import profileService from "@base/modules/user/profile/profileService";
import {
  formatSlot,
  getContactPhone,
  getExtraFieldItems,
  getGroupExamItems,
  getServiceUsageCard,
  getTransferContent,
  isCancelBlockedByPayment,
  isPackageAppointment,
  isUnpaidAppointment,
} from "@base/modules/appointment/appointmentDetailRules";
import { getAppointmentUsedService, getServiceChannel } from "@base/modules/appointment/appointmentUsedService";
import useGetByRefCode from "@base/modules/order/hooks/useGetByRefCode";
import { baseRoutes } from "@base/routes/baseRoutes";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";
import CommonStyles from "@components/CommonStyles";
import CollapsibleText from "@components/CommonStyles/CollapsibleText";
import TagAppointmentStatus from "@components/CommonStyles/TagAppointmentStatus";
import TagAppointmentType from "@components/CommonStyles/TagAppointmentType";
import CardTransferInfo from "@components/CommonStyles/CardTransferInfo";
import AppointmentAttachments from "./AppointmentAttachments";
import AppointmentRatingSection from "./CheckoutSurvey/AppointmentRatingSection";
import DetailCard from "./DetailCard";
import ServiceItemsPicker from "@components/User/Package/PackageDetail/ServiceItemsPicker";
import ModalAppointmentCancelling from "./ModalAppointmentCancelling";

/** One fact of the appointment: an icon and its text, left out entirely when there is no text. */
const InfoRow = ({ icon, children }: { icon: React.ReactNode; children?: React.ReactNode }) => {
  if (children === undefined || children === null || children === "") {
    return null;
  }

  return (
    <div className="flex items-start gap-3">
      {/* A fixed-width slot, so every text starts at the same x whatever the icon. */}
      <span className="flex-none flex items-center justify-center w-6 h-6 text-lg text-common-primary">{icon}</span>
      <div className="grow min-w-0 text-sm lg:text-base font-['Inter'] text-common-maintext leading-6">{children}</div>
    </div>
  );
};

export default function AppointmentDetail() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { key } = useParams();
  const dispatch = useDispatch();
  const { data: resultOrder, fetch: fetchResultOrder } = useGetByRefCode();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<unknown>();
  const [appointment, setAppointment] = useState<IAppointment>({});

  const [showCancelling, setShowCancelling] = useState<boolean>(false);
  // The account's full records, only to find a contact phone the appointment's short record lacks.
  const [fullRecords, setFullRecords] = useState<IPatientRecord[]>([]);

  const { open: openQr, shouldRender: shouldRenderQr, toggle: toggleQr } = useToggle();
  const { open: openTransfer, shouldRender: shouldRenderTransfer, toggle: toggleTransfer } = useToggle();
  const { open: openGroupExam, shouldRender: shouldRenderGroupExam, toggle: toggleGroupExam } = useToggle();
  const { hotline, hasBankAccount } = useCommonSettings();

  const usedService = getAppointmentUsedService(appointment);
  // Where the service happens replaces the booking site, its address and the doctor: for a package
  // those name the seller (e.g. INSMART), not where the patient has to be.
  const usageCard = getServiceUsageCard(appointment);
  const extraFields = getExtraFieldItems(appointment, i18n.resolvedLanguage);
  const groupExamItems = getGroupExamItems(appointment);
  const siteName = appointment.site_name_show?.trim() || appointment.site_name?.trim();
  const siteAddress = appointment.site_address_show?.trim() || appointment.site_address?.trim();
  const doctorName = appointment.doctor_name_show?.trim() || appointment.doctor?.doctor_name?.trim();
  const serviceNames = appointment.appointment_used_services
    ?.map((service) => service.name?.trim())
    .filter(Boolean)
    .join(", ");
  const record = appointment.patient_record;
  const isPending = appointment.status === EAppointmentStatus.PENDING;
  const isCancelled = appointment.status === EAppointmentStatus.CANCELLED;
  const isUnpaid = isUnpaidAppointment(appointment);
  const contactPhone = getContactPhone(appointment, fullRecords);
  const promotion = appointment.appointment_promotions?.[0]?.description;
  const isUpcomingOnline =
    appointment.appointment_type === EAppointmentType.ONLINE && (isPending || appointment.status === EAppointmentStatus.IN_PROGRESS);
  // The reminder needs the default transfer account to point at.
  const showsTransferReminder = isPending && appointment.payment_status === false && hasBankAccount;

  const getAppointment = useCallback(async () => {
    if (!key) {
      return;
    }

    try {
      setIsLoading(true);
      setError(undefined);
      const resp = await appointmentService.getByKey(key);
      setAppointment(resp.data);
    } catch (error) {
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, [key]);

  useEffect(() => {
    getAppointment();
  }, [getAppointment]);

  // Only a package booking whose own record has no contact phone needs the full list.
  const needsFullRecords = isPackageAppointment(appointment) && !appointment.contact_phone?.trim() && !appointment.patient_record?.contact_phone?.trim();

  useEffect(() => {
    if (!needsFullRecords) {
      return;
    }

    let ignore = false;

    profileService
      .getByUserKey()
      .then((resp) => !ignore && setFullRecords((resp.data ?? []).map((item) => item.patient_record).filter((record): record is IPatientRecord => !!record)))
      .catch(() => undefined);

    return () => {
      ignore = true;
    };
  }, [needsFullRecords]);

  useEffect(() => {
    if (appointment.key) {
      // The order API speaks only vi and en.
      fetchResultOrder({ ref_code: appointment.key, order_type: EOrderType.APPT, lang: i18n.resolvedLanguage === "en" ? "en" : "vi" });
    }
  }, [appointment]);

  const onClickCancelAppt = () => {
    // Kept visible on purpose, so the patient learns why a paid booking cannot be cancelled here.
    if (isCancelBlockedByPayment(appointment)) {
      dispatch(setAlertMessage({ type: "warning", message: t("Notification"), description: t("appointmentDetail.cancelUnavailable", { hotline }) }));
      return;
    }

    setShowCancelling(true);
  };

  const onOpenTransfer = () => {
    // Without the order there is nothing reliable to show.
    if (resultOrder?.ref_key || resultOrder?.order_key) {
      toggleTransfer();
    }
  };

  const handlePayAppt = () => {
    if (!resultOrder?.order_key) {
      dispatch(setAlertMessage({ type: "error", message: t("Notification"), description: "Không thể thanh toán! Vui lòng thử lại sau." }));
      return;
    }

    navigate(baseRoutes.userPaymentDetail.replace(":key", resultOrder.order_key));
  };

  if (isLoading) {
    return <CommonStyles.LoadingUI />;
  }

  if (error || !appointment.key) {
    const isNotFound = (error as AxiosError)?.response?.status === 404 || !error;

    return (
      <div className="p-5 py-16 flex flex-col items-center gap-4 text-center">
        <span className="text-base font-medium font-['Inter'] text-common-maintext">
          {isNotFound ? t("appointmentDetail.notFound") : t("appointmentDetail.loadFailed")}
        </span>
        {!isNotFound ? (
          <CommonStyles.ButtonUI className="h-10 px-6 font-medium" onClick={getAppointment}>
            {t("Retry")}
          </CommonStyles.ButtonUI>
        ) : null}
      </div>
    );
  }

  const genderAndDob = [
    record?.patient_gender === false ? t("appointmentDetail.female") : t("appointmentDetail.male"),
    record?.patient_dob ? dayjs(record.patient_dob, "YYYY-MM-DD").format("DD/MM/YYYY") : null,
    contactPhone,
  ]
    .filter(Boolean)
    .join(" - ");

  return (
    <div className="p-5">
      <div className="text-common-primary text-base font-bold font-['Inter']">{t("user.appointment.appointmentDetail")}</div>

      <Divider />

      <div className="flex flex-col gap-4">
        {/* Who the appointment is for; the code opens as a QR to show at the facility. */}
        <DetailCard>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
            <div className="grow basis-60 min-w-0 flex items-center gap-4">
              <img className="flex-none w-14 h-14 rounded-full border border-solid border-common-divider" src={CommonImages.DefaultAvatar} alt="" />
              <div className="min-w-0 flex flex-col">
                <span className="text-base lg:text-lg font-semibold font-['Inter'] text-common-maintext break-words">{record?.patient_name}</span>
                <span className="text-sm font-['Inter'] text-common-subtext">{genderAndDob}</span>
              </div>
            </div>
            <CommonStyles.ButtonUI className="h-10 font-medium max-sm:w-full" icon={<QrcodeOutlined />} ghost onClick={toggleQr}>
              {t("appointmentDetail.qrTitle")}
            </CommonStyles.ButtonUI>
          </div>
        </DetailCard>

        {showsTransferReminder ? <CardTransferInfo showDetail={onOpenTransfer} /> : null}

        <DetailCard
          title={t("appointmentDetail.appointmentInfo")}
          extra={
            <div className="[&_.ant-tag]:me-0">
              <TagAppointmentStatus status={appointment.status as EAppointmentStatus} />
            </div>
          }
        >
          <div className="flex flex-col gap-3">
            {isUnpaid ? (
              <InfoRow icon={<ExclamationCircleOutlined className="text-common-red500" />}>
                <span className="font-medium text-common-red500">{t("appointmentDetail.unpaid")}</span>
              </InfoRow>
            ) : null}
            {/* The same badge as the appointment list, so a booking reads the same in both places. */}
            <div className="flex [&_.ant-tag]:me-0">
              <TagAppointmentType type={appointment.appointment_type as EAppointmentType} channel={getServiceChannel(usedService)} />
            </div>
            <InfoRow icon={<CalendarOutlined />}>{formatSlot(appointment.time_table_period?.period_start_time, i18n.resolvedLanguage)}</InfoRow>
            {!usageCard ? (
              <>
                <InfoRow icon={<CommonIcons.IconDoctorSite size={18} />}>
                  {siteName ? [siteName, appointment.site_branch_name].filter(Boolean).join(" - ") : null}
                </InfoRow>
                <InfoRow icon={<EnvironmentOutlined />}>{siteAddress}</InfoRow>
                <InfoRow icon={<CommonIcons.IconDoctorPerson size={20} />}>{doctorName}</InfoRow>
              </>
            ) : null}
            <InfoRow icon={<MedicineBoxOutlined />}>{serviceNames}</InfoRow>
            {groupExamItems.length ? (
              <button
                type="button"
                onClick={toggleGroupExam}
                className="self-start ml-9 p-0 bg-transparent border-0 text-sm lg:text-base font-medium font-['Inter'] text-common-inprogress cursor-pointer"
              >
                {t("appointmentDetail.groupExamDetail")}
              </button>
            ) : null}
            <InfoRow icon={<FileTextOutlined />}>{appointment.symptoms?.trim() ? <CollapsibleText text={appointment.symptoms} /> : null}</InfoRow>
          </div>
        </DetailCard>

        {usageCard ? (
          <DetailCard title={t("packageBooking.serviceAddress")}>
            <div className="flex flex-col gap-0.5 text-sm lg:text-base font-['Inter']">
              {usageCard.name ? <span className="font-semibold text-common-primary">{usageCard.name}</span> : null}
              <span className="text-common-maintext">{usageCard.address}</span>
            </div>
          </DetailCard>
        ) : null}

        {/* Whatever the package asked the patient for at booking time, shown back read-only. */}
        {extraFields.length ? (
          <DetailCard title={t("packageBooking.extraInformation")}>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {extraFields.map((field, index) => (
                <div key={`${field.label}-${index}`} className="flex flex-col gap-0.5 font-['Inter']">
                  <span className="text-sm text-common-subtext">{field.label}</span>
                  <span className="text-sm lg:text-base text-common-maintext">{field.value}</span>
                </div>
              ))}
            </div>
          </DetailCard>
        ) : null}

        {isCancelled && appointment.cancel_reason?.trim() ? (
          <DetailCard title={t("appointmentDetail.cancelReason")}>
            <span className="text-sm lg:text-base font-['Inter'] text-common-maintext whitespace-pre-line">{appointment.cancel_reason}</span>
          </DetailCard>
        ) : null}

        <AppointmentAttachments appointment={appointment} onChanged={getAppointment} />

        {promotion ? (
          <div className="flex items-center gap-3 p-3 rounded-2xl border border-solid border-common-primary200 bg-common-primary50">
            <span className="flex-none flex items-center justify-center w-10 h-10 rounded-xl bg-common-primary text-common-white text-lg">
              <GiftOutlined />
            </span>
            <span className="text-sm lg:text-base font-['Inter'] text-common-primary">{promotion}</span>
          </div>
        ) : null}

        {/* Online consultations happen in the app, so an upcoming one points there. */}
        {isUpcomingOnline ? (
          <div className="flex justify-between items-center gap-6 p-4 rounded-2xl border border-solid border-common-blue600 bg-common-blue100">
            <div className="grow flex flex-col gap-2">
              <span className="text-common-blue700 text-base font-medium font-['Inter']">{t("appointmentDetail.appTitle")}</span>
              <span className="text-common-subtext text-sm font-['Inter']">{t("appointmentDetail.appDescription")}</span>
            </div>
            <img src={CommonImages.medihomeQr} alt="" className="flex-none h-24" />
          </div>
        ) : null}

        {/* Every way into rating goes through here, so a survey appointment is always surveyed first. */}
        {appointment.id ? <AppointmentRatingSection appointment={appointment} /> : null}

        {isUnpaid || isPending ? (
          <div className="flex flex-wrap justify-end gap-3 pt-2">
            {isPending ? (
              <CommonStyles.ButtonUI className="h-10 px-6 font-medium" danger ghost onClick={onClickCancelAppt}>
                {t("user.appointment.btnCancelAppt")}
              </CommonStyles.ButtonUI>
            ) : null}

            {/* A completed booking still unpaid keeps the button, but it can no longer be paid here. */}
            {isUnpaid ? (
              <CommonStyles.ButtonUI
                className="h-10 px-6 font-medium"
                type="primary"
                disabled={appointment.status === EAppointmentStatus.COMPLETED}
                onClick={handlePayAppt}
              >
                {t("payNow")}
              </CommonStyles.ButtonUI>
            ) : null}
          </div>
        ) : null}
      </div>

      <ModalAppointmentCancelling appointment={appointment} open={showCancelling} onClose={() => setShowCancelling(false)} />

      {shouldRenderQr && (
        <CommonStyles.ModalConfirmationUI
          open={openQr}
          toggle={toggleQr}
          type="normal"
          hiddenAction
          width={360}
          title={t("appointmentDetail.qrTitle")}
          content={
            <div className="flex flex-col items-center gap-3 text-center">
              <QRCode value={String(appointment.key)} size={220} bordered={false} />
              <span className="text-xs font-['Inter'] text-common-subtext break-all">{appointment.key}</span>
              <span className="text-sm font-['Inter'] text-common-maintext">{t("appointmentDetail.qrHint")}</span>
            </div>
          }
        />
      )}

      {shouldRenderTransfer && (
        <CommonStyles.TransferInformationDialog
          open={openTransfer}
          toggle={toggleTransfer}
          resultOrder={resultOrder}
          transferContent={getTransferContent(appointment)}
        />
      )}

      {shouldRenderGroupExam && (
        <ServiceItemsPicker
          open={openGroupExam}
          toggle={toggleGroupExam}
          mode="VIEW"
          title={usedService.used?.name}
          // Stored with the booking as the chosen items only; an older booking may flag them itself.
          items={groupExamItems.map((item) => ({ ...item, id: Number(item.id) || undefined, selected: item.selected !== false }))}
          cap={0}
        />
      )}
    </div>
  );
}
